import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import "./VersionControl.css";

const CONFIG_COLLECTION = "app_config";
const DOC_IDS = {
  user: "version_control_user",
  partner: "version_control_partner",
};

const emptyForm = () => ({
  android_min_version: "",
  ios_min_version: "",
  android_store_url: "",
  ios_store_url: "",
  force_update: false,
});

const VersionControl = () => {
  const [activeApp, setActiveApp] = useState("user");
  const [form, setForm] = useState(emptyForm());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const currentDocId = DOC_IDS[activeApp];

  const fetchConfig = async (appKey) => {
    setError(null);
    try {
      const ref = doc(db, CONFIG_COLLECTION, DOC_IDS[appKey]);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        return {
          android_min_version: data.android_min_version || "",
          ios_min_version: data.ios_min_version || "",
          android_store_url: data.android_store_url || "",
          ios_store_url: data.ios_store_url || "",
          force_update: data.force_update === true,
        };
      }
    } catch (err) {
      setError("Failed to load version control settings.");
    }
    return emptyForm();
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const data = await fetchConfig(activeApp);
      if (!cancelled) {
        setForm(data);
      }
      setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [activeApp]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const ref = doc(db, CONFIG_COLLECTION, currentDocId);
      const payload = {
        android_min_version: form.android_min_version || "",
        ios_min_version: form.ios_min_version || "",
        android_store_url: form.android_store_url || "",
        ios_store_url: form.ios_store_url || "",
        force_update: !!form.force_update,
      };
      await setDoc(ref, payload, { merge: true });
      setMessage(`${activeApp === "user" ? "User" : "Partner"} app settings updated successfully.`);
    } catch (err) {
      setError("Failed to save version control settings.");
    } finally {
      setSaving(false);
    }
  };

  const handleSwitchApp = (appKey) => {
    if (appKey === activeApp) return;
    setActiveApp(appKey);
    setMessage(null);
    setError(null);
  };

  return (
    <div className="version-control-page">
      <h1 className="page-title">Version Control</h1>
      <p className="page-subtitle">
        Manage minimum app versions and force update behaviour for Android and iOS. Two documents: one for User app, one for Partner app.
      </p>

      <div className="app-tabs">
        <button
          type="button"
          className={`app-tab ${activeApp === "user" ? "active" : ""}`}
          onClick={() => handleSwitchApp("user")}
        >
          User app
        </button>
        <button
          type="button"
          className={`app-tab ${activeApp === "partner" ? "active" : ""}`}
          onClick={() => handleSwitchApp("partner")}
        >
          Partner app
        </button>
      </div>

      {loading ? (
        <div className="version-control-card">
          <p className="loading-message">Loading version settings...</p>
        </div>
      ) : (
        <div className="version-control-card">
          <form onSubmit={handleSubmit} className="form-grid">
            <div className="form-group">
              <label htmlFor="android_min_version">Android minimum version</label>
              <input
                id="android_min_version"
                name="android_min_version"
                type="text"
                className="form-control"
                placeholder="e.g. 2.3.0"
                value={form.android_min_version}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="ios_min_version">iOS minimum version</label>
              <input
                id="ios_min_version"
                name="ios_min_version"
                type="text"
                className="form-control"
                placeholder="e.g. 2.3.0"
                value={form.ios_min_version}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="android_store_url">Android Play Store URL</label>
              <input
                id="android_store_url"
                name="android_store_url"
                type="url"
                className="form-control"
                placeholder="https://play.google.com/store/apps/details?id=com.app"
                value={form.android_store_url}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="ios_store_url">iOS App Store URL</label>
              <input
                id="ios_store_url"
                name="ios_store_url"
                type="url"
                className="form-control"
                placeholder="https://apps.apple.com/app/id123456"
                value={form.ios_store_url}
                onChange={handleChange}
              />
            </div>

            <div className="form-group checkbox-group">
              <label htmlFor="force_update" className="checkbox-label">
                <input
                  id="force_update"
                  name="force_update"
                  type="checkbox"
                  checked={form.force_update}
                  onChange={handleChange}
                />
                <span>Force update (block old app versions)</span>
              </label>
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="primary-btn"
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Settings"}
              </button>
            </div>
          </form>

          {message && <p className="success-text">{message}</p>}
          {error && <p className="error-text">{error}</p>}

          <div className="current-config-section">
            <h3 className="current-config-title">Current configuration ({activeApp === "user" ? "User app" : "Partner app"})</h3>
            <p className="current-config-note">
              Document: <code>{CONFIG_COLLECTION}</code> / <code>{currentDocId}</code>. Saving above patches this document.
            </p>
            <dl className="current-config-list">
              <div className="current-config-row">
                <dt>Android minimum version</dt>
                <dd>{form.android_min_version || "—"}</dd>
              </div>
              <div className="current-config-row">
                <dt>iOS minimum version</dt>
                <dd>{form.ios_min_version || "—"}</dd>
              </div>
              <div className="current-config-row">
                <dt>Android Play Store URL</dt>
                <dd>{form.android_store_url ? <a href={form.android_store_url} target="_blank" rel="noopener noreferrer" className="config-link">{form.android_store_url}</a> : "—"}</dd>
              </div>
              <div className="current-config-row">
                <dt>iOS App Store URL</dt>
                <dd>{form.ios_store_url ? <a href={form.ios_store_url} target="_blank" rel="noopener noreferrer" className="config-link">{form.ios_store_url}</a> : "—"}</dd>
              </div>
              <div className="current-config-row">
                <dt>Force update</dt>
                <dd>{form.force_update ? "Yes" : "No"}</dd>
              </div>
            </dl>
          </div>
        </div>
      )}
    </div>
  );
};

export default VersionControl;

